/**
 * The two questions a person is asked:
 *
 * - `Question`, one question of the brief conversation (docs/SPEC.md §5.2):
 *   its 2–4 choices with the model's own answer already selected, **That's
 *   right**, a free box, and **You decide**. Picking another choice turns
 *   the button into **Choose this**; both send the chosen index.
 * - `ChangeQuestion`, a change's question (§8.5): "Which one?", the
 *   question's words, its 2–4 choices and a free box. A choice that is a
 *   scene of another film or a template (§12) shows that scene, framed by
 *   the player in the browser from the film's manifest (`question.scenes`,
 *   P28-T10: no stills).
 */
import { useState } from "react";
import type { AnswerBody } from "../api/brief";
import type { BriefQuestion } from "../api/ads";
import type { Change, ChangeAnswer, QuestionScene } from "../api/changes";
import { useManifest } from "../api/versions";
import { CONVERSATION, say } from "../copy/brief";
import { BOX } from "../copy/changes";
import { frameSize } from "../film/frames";
import { useFrame } from "../film/useFrame";
import { Button } from "../ui";

export function Question({
  question,
  number,
  busy,
  onAnswer,
}: {
  question: BriefQuestion;
  /** Which question this is (1–3). */
  number: number;
  /** A turn is running or an answer is in flight: nothing can be sent. */
  busy: boolean;
  onAnswer: (body: AnswerBody) => void;
}) {
  const [picked, setPicked] = useState(question.selected);
  const [text, setText] = useState("");
  const words = text.trim();
  return (
    <div
      className="flex flex-col gap-3 rounded-lg border border-brand-light/60 bg-brand-tint/30 p-4"
      data-testid="question"
    >
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {say(CONVERSATION.questionOf, { n: number })}
      </div>
      <p className="m-0 text-base font-semibold text-slate-900">
        {question.text}
      </p>
      <div
        role="radiogroup"
        aria-label={question.text}
        className="flex flex-wrap gap-2"
      >
        {question.choices.map((choice, i) => {
          const checked = i === picked;
          return (
            <button
              key={`${i}-${choice}`}
              type="button"
              role="radio"
              aria-checked={checked}
              disabled={busy}
              onClick={() => setPicked(i)}
              className={[
                "min-h-[44px] rounded-pill border px-4 py-2 text-left text-sm transition-colors md:min-h-0",
                checked
                  ? "border-brand bg-white font-semibold text-brand-dark shadow-glow"
                  : "border-slate-200 bg-white text-slate-700 hover:border-brand-light",
              ].join(" ")}
            >
              {choice}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="primary"
          disabled={busy}
          onClick={() => onAnswer({ choice: picked })}
        >
          {picked === question.selected
            ? CONVERSATION.thatsRight
            : CONVERSATION.chooseThis}
        </Button>
        <Button
          type="button"
          variant="ghost"
          disabled={busy}
          title={CONVERSATION.youDecideHint}
          onClick={() => onAnswer({ you_decide: true })}
        >
          {CONVERSATION.youDecide}
        </Button>
      </div>
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!words || busy) return;
          onAnswer({ text: words });
          setText("");
        }}
      >
        <input
          className="tb-input min-w-0 flex-1"
          aria-label={CONVERSATION.freeLabel}
          placeholder={CONVERSATION.freePlaceholder}
          value={text}
          maxLength={4000}
          disabled={busy}
          onChange={(e) => setText(e.target.value)}
        />
        <Button type="submit" disabled={busy || !words}>
          {CONVERSATION.send}
        </Button>
      </form>
    </div>
  );
}

/** A candidate scene's picture: 160 px wide at most, drawn at 320. */
const CANDIDATE_LONG = 320;

function CandidateFrame({ scene, i }: { scene: QuestionScene; i: number }) {
  const manifest = useManifest(scene.manifest_url);
  const aspect = manifest.data?.aspect ?? "16:9";
  const [w, h] = aspect.split(":").map(Number);
  const frame = useFrame(
    manifest.data ?? null,
    scene.scene,
    frameSize(
      aspect,
      Math.round((CANDIDATE_LONG * Math.min(w, h)) / Math.max(w, h)),
    ),
  );
  return frame ? (
    <img
      src={frame}
      alt=""
      className="aspect-video w-full rounded object-cover"
      data-testid={`choice-frame-${i}`}
    />
  ) : (
    <span className="block aspect-video w-full rounded bg-slate-100" />
  );
}

export function ChangeQuestion({
  change,
  busy,
  onAnswer,
}: {
  change: Change;
  busy: boolean;
  onAnswer(answer: ChangeAnswer): void;
}) {
  const q = change.question!;
  const [text, setText] = useState("");
  const words = text.trim();
  return (
    <div
      className="flex flex-col gap-3 rounded-lg border border-brand-light/60 bg-brand-tint/30 p-3"
      data-testid="change-question"
    >
      <p className="m-0 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {BOX.questionFrame}
      </p>
      {change.say ? (
        <p className="m-0 text-sm text-slate-800">{change.say}</p>
      ) : null}
      {q.text && q.text !== BOX.questionFrame ? (
        <p className="m-0 text-base font-semibold text-slate-900">{q.text}</p>
      ) : null}
      <div
        className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap"
        role="group"
        aria-label={q.text || BOX.questionFrame}
      >
        {q.choices.map((choice, i) => {
          const scene = q.scenes?.[i] ?? null;
          const picked = q.selected === i;
          return (
            <button
              key={`${i}-${choice.name}`}
              type="button"
              disabled={busy}
              onClick={() => onAnswer({ choice: i })}
              data-testid={`choice-${i}`}
              className={[
                "flex min-h-[44px] flex-col items-stretch gap-1 rounded-lg border bg-white p-1.5 text-left text-sm transition-colors sm:max-w-[10rem]",
                picked
                  ? "border-brand font-semibold text-brand-dark shadow-glow"
                  : "border-slate-200 text-slate-700 hover:border-brand-light",
              ].join(" ")}
            >
              {scene ? <CandidateFrame scene={scene} i={i} /> : null}
              <span className="px-1">{choice.name}</span>
            </button>
          );
        })}
      </div>
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!words || busy) return;
          onAnswer({ text: words });
          setText("");
        }}
      >
        <input
          className="tb-input min-w-0 flex-1"
          aria-label={BOX.orSay}
          placeholder={BOX.orSay}
          value={text}
          maxLength={2000}
          disabled={busy}
          onChange={(e) => setText(e.target.value)}
        />
        <Button type="submit" size="sm" disabled={busy || !words}>
          {BOX.answer}
        </Button>
      </form>
    </div>
  );
}
